import { TOUR_DEFAULT_PLACEMENT, TOUR_TARGET_WAIT_MS } from "../constants/tour";
import { TOUR_TARGETS, tourSelector } from "../constants/tourTargets";
import { waitForElement } from "../utils/helper";

// Ordered tour content. needsEditorSurface and needsTabView say which view a
// stop's target only exists in, so the hook can put the studio there before the
// tour arrives. Both are opt in, because closing a preview the user opened
// costs them a full regeneration to get back: reopening it re-runs the whole
// document build. Anything anchored in the header reads the same either way and
// says nothing here.
//
// No stop carries a requirement. The other two tours gate on work the user has
// to do, because both are wizards collecting facts. The studio is a review
// surface over a letter that already exists, and the only actions worth asking
// for here would write into that letter: a stop demanding a revision comment
// would put tour text into a real demand and queue a regeneration off the back
// of it. So this one explains and never blocks.
const DEMAND_STUDIO_TOUR_STOPS = [
   {
      target: TOUR_TARGETS.DEMAND_STUDIO_VIEW_TOGGLE,
      title: "Two ways to read it",
      content:
         "Tab view takes the letter a section at a time. Scroll view lays the whole thing out end to end, which is the one to use for a final read through.",
      placement: "bottom",
   },
   {
      target: TOUR_TARGETS.DEMAND_STUDIO_TABS,
      needsEditorSurface: true,
      needsTabView: true,
      title: "Jump straight to a section",
      content:
         "Every section of the demand has its own tab. What you see below, and the comments on the right, follow whichever one is selected.",
      placement: "bottom",
   },
   {
      target: TOUR_TARGETS.DEMAND_STUDIO_DOCUMENT,
      needsEditorSurface: true,
      title: "Edit the text directly",
      content:
         "This is the letter itself, and you can type into it. Edits save as plain text and stay exactly as you wrote them, so anything you are certain about is quicker to fix here than to ask for.",
   },
   {
      target: TOUR_TARGETS.DEMAND_STUDIO_COMMENTS,
      needsEditorSurface: true,
      title: "Or ask for a rewrite",
      content:
         "Say what you want changed and the section is rewritten for you, leaving the rest of the letter alone. Better than editing by hand when you want a different argument rather than different words.",
      placement: "left",
   },
   {
      target: TOUR_TARGETS.DEMAND_STUDIO_TOOLS,
      title: "Search, undo and reminders",
      content:
         "Search runs across every section, not just the one showing. Undo and redo cover both your edits and any rewrites. The lightbulb keeps the notes on what the draft can and cannot be trusted with, which is worth reading once.",
      placement: "bottom",
   },
   {
      target: TOUR_TARGETS.DEMAND_STUDIO_PREVIEW,
      // the button swaps out for Close Preview once the preview is up
      needsEditorSurface: true,
      title: "See the finished letter",
      content:
         "Builds the formatted document from where the draft stands now, exhibits included, so you can read it as the adjuster will. Come back and keep editing afterwards.",
      placement: "bottom",
   },
   {
      target: TOUR_TARGETS.DEMAND_STUDIO_SAVE,
      title: "Save and return when you are done",
      content:
         "Your edits are applied and the demand is rebuilt around them. Nothing is lost if you leave earlier, but the letter is only regenerated when you come through here.",
      placement: "bottom",
   },
];

// The stops the tour visits, in order. Fixed rather than case dependent, so the
// steps array cannot resize under a running tour and leave the index past the end
export const getDemandStudioTourStops = () => DEMAND_STUDIO_TOUR_STOPS;

export const buildDemandStudioTourSteps = ({ prepareSurface }) =>
   DEMAND_STUDIO_TOUR_STOPS.map(
      ({ target, needsEditorSurface, needsTabView, title, content, placement }) => {
         const selector = tourSelector(target);

         return {
            target: selector,
            title,
            content,
            placement: placement ?? TOUR_DEFAULT_PLACEMENT,
            // Joyride calls .then on this unconditionally, so it always
            // resolves. A step with a before hook never reaches the library's
            // own target polling, and an anchor that is not mounted the moment
            // this settles is skipped outright, so the wait belongs here
            before: async () => {
               await prepareSurface({ needsEditorSurface, needsTabView });
               await waitForElement({ selector, timeoutMs: TOUR_TARGET_WAIT_MS });
            },
         };
      }
   );

export default DEMAND_STUDIO_TOUR_STOPS;
